import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import PatientService from "../services/PatientService";
import VisitService from "../services/VisitService";
import logo from "../assets/wmedic-logo.png";

const styles = {
    pageWrapper: { padding: "30px", backgroundColor: "#f8f9fa", minHeight: "100vh", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
    container: { maxWidth: "1000px", margin: "0 auto", backgroundColor: "#fff", padding: "40px", borderRadius: "15px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" },
    chartWrapper: { 
        height: "350px", // Fiksna visina je ključna za ResponsiveContainer
        width: "100%", 
        marginBottom: "40px", 
        padding: "20px", 
        border: "1px solid #f0f0f0", 
        borderRadius: "10px",
        backgroundColor: "#fff"
    },
    header: { borderBottom: "3px solid #B8860B", marginBottom: "30px", paddingBottom: "15px", textAlign: "center" },
    btnBack: { padding: "10px 20px", cursor: "pointer", borderRadius: "8px", border: "1px solid #ddd", background: "#fff", fontWeight: "600" },
    btnPdf: { padding: "10px 25px", background: "#28a745", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", boxShadow: "0 2px 5px rgba(40,167,69,0.3)" },
    noData: { textAlign: "center", padding: "20px", color: "#888", fontStyle: "italic" }
};

const ParameterChart = ({ title, dataKey, color, data }) => {
    // Provera da li uopšte postoje podaci za ovaj ključ
    const hasData = data.some(d => d[dataKey] !== undefined && d[dataKey] !== null);
    
    if (!hasData) return null;

    return (
        <div style={styles.chartWrapper}>
            <h4 style={{ marginBottom: "20px", color: "#444", fontSize: "18px", borderLeft: `5px solid ${color}`, paddingLeft: "10px" }}>{title}</h4>
            <ResponsiveContainer width="100%" height="90%">
                <LineChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis 
                        dataKey="datum" 
                        tick={{fontSize: 12}} 
                        interval="preserveStartEnd"
                    />
                    <YAxis tick={{fontSize: 12}} domain={['auto', 'auto']} />
                    <Tooltip contentStyle={{ borderRadius: "10px", border: "none", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }} />
                    <Legend verticalAlign="top" height={36}/>
                    <Line 
                        name={title}
                        type="monotone" 
                        dataKey={dataKey} 
                        stroke={color} 
                        strokeWidth={4} 
                        dot={{ r: 6, fill: color, strokeWidth: 2, stroke: "#fff" }} 
                        activeDot={{ r: 8 }}
                        connectNulls 
                        animationDuration={1000}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

const PatientProgress = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const pdfRef = useRef();
    const [patient, setPatient] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
    const fetchData = async () => {
        try {
            setLoading(true);
            const [pData, vData] = await Promise.all([
                PatientService.getPatientById(id),
                VisitService.getAllVisitsByPatientId(id)
            ]);
            
            setPatient(pData);

            // LOG ZA DEBUG - Otvori Console (F12) u browseru i vidi šta piše
            console.log("Sve posete iz baze:", vData);

            const filtered = (vData || []).filter(v => 
                v.visitItemDTOS?.some(item => {
                    const typeId = Number(item.treatmentTypeDTO?.treatmentTypeID);
                    const typeName = item.treatmentTypeDTO?.typeName?.toLowerCase();
                    // Proveravamo ili ID 3 ili naziv "auriculotherapy"
                    return typeId === 3 || typeName === "auriculotherapy";
                })
            ).sort((a, b) => new Date(a.visitDate) - new Date(b.visitDate));

            console.log("Filtrirane posete za grafikon:", filtered);
            setHistory(filtered);
        } catch (error) {
            console.error("Greška pri učitavanju podataka", error);
        } finally {
            setLoading(false);
        }
    };
    fetchData();
}, [id]);

const chartData = useMemo(() => {
    return history.map(v => {
        // Pronalazimo pravi item unutar posete
        const item = v.visitItemDTOS.find(i => {
            const typeId = Number(i.treatmentTypeDTO?.treatmentTypeID);
            const typeName = i.treatmentTypeDTO?.typeName?.toLowerCase();
            return typeId === 3 || typeName === "auriculotherapy";
        });

        const d = item?.treatmentTypeDTO;

        return {
            // Skraćeni format datuma: "02.04."
            datum: new Date(v.visitDate).toLocaleDateString("sr-RS", { day: '2-digit', month: '2-digit' }),
            težina: d?.weight || null,
            bmi: d?.bmi || null,
            masti: d?.bodyFat || null,
            mišići: d?.protein || null,
            visceralne: d?.visceralFat || null,
            struk: d?.waist || null,
            stomak: d?.abdomenCircumference || null,
            kukovi: d?.hipCircumference || null,
            grudi: d?.chestCircumference || null
        };
    });
}, [history]);

    const downloadPDF = async () => {
    const element = pdfRef.current;
    const buttons = document.querySelector('.no-pdf-tools');
    
    // 1. Sakrij dugmiće
    if (buttons) buttons.style.display = 'none';

    try {
        // 2. Kreiraj canvas sa većim scale-om za bolji kvalitet
        const canvas = await html2canvas(element, { 
            scale: 2, 
            useCORS: true,
            logging: false,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        
        // Izračunaj visinu slike u PDF jedinicama (mm)
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
        
        let heightLeft = imgHeight;
        let position = 0;

        // 3. Dodavanje prve stranice
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;

        // 4. Dodavanje ostalih stranica ako sadržaj preostaje
        while (heightLeft > 0) {
            position = heightLeft - imgHeight; // Pomeri sliku "nagore" za visinu prethodne strane
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
            heightLeft -= pdfHeight;
        }

        pdf.save(`Izvestaj_Napredak_${patient?.name}_${patient?.surname}.pdf`);
    } catch (error) {
        console.error("Greška pri generisanju PDF-a:", error);
    } finally {
        // 5. Vrati dugmiće
        if (buttons) buttons.style.display = 'flex';
    }
};
    if (loading) return <div style={{textAlign: "center", padding: "50px"}}>Učitavanje grafikona...</div>;

    return (
        <div style={styles.pageWrapper}>
            <div style={{ maxWidth: "1000px", margin: "0 auto 20px auto", display: "flex", justifyContent: "space-between" }} className="no-pdf-tools">
                <button onClick={() => navigate(-1)} style={styles.btnBack}>← Nazad u karton</button>
                <button onClick={downloadPDF} style={styles.btnPdf}>Preuzmi PDF Izveštaj 📄</button>
            </div>

            <div ref={pdfRef} style={styles.container}>
                <div style={styles.header}>
                    <img src={logo} alt="W Medic" style={{ maxWidth: "120px", marginBottom: "10px" }} />
                    <h2 style={{ color: "#B8860B", margin: "0 0 5px 0" }}>W MEDIC</h2>
                    <p style={{ color: "#666", margin: 0 }}> Milorada Šapčanina 4a, Šabac, Srbija</p>
                    <p style={{ color: "#666", margin: 0 }}> +381 66 862 3785</p>
                    <p style={{ color: "#666", margin: 0 }}>  Izveštaj o napretku pacijenta</p>
                    <h3 style={{ marginTop: "20px", color: "#333" }}>{patient?.name} {patient?.surname}</h3>
                </div>

                {chartData.length > 0 ? (
                    <>
                        <ParameterChart title="Težina (kg)" dataKey="težina" color="#28a745" data={chartData} />
                        <ParameterChart title="BMI" dataKey="bmi" color="#B8860B" data={chartData} />
                        <ParameterChart title="Masti (%)" dataKey="masti" color="#e53e3e" data={chartData} />
                        <ParameterChart title="Mišići / Proteini (%)" dataKey="mišići" color="#319795" data={chartData} />
                        <ParameterChart title="Visceralne masti" dataKey="visceralne" color="#d69e2e" data={chartData} />
                        <ParameterChart title="Obim grudi (cm)" dataKey="grudi" color="#e53e3e" data={chartData} />
                        <ParameterChart title="Obim kukova (cm)" dataKey="kukovi" color="#007bff" data={chartData} />
                        <ParameterChart title="Obim struka (cm)" dataKey="struk" color="#805ad5" data={chartData} />
                        <ParameterChart title="Obim stomaka (cm)" dataKey="stomak" color="#ed8936" data={chartData} />
                    </>
                ) : (
                    <div style={styles.noData}>Nema dovoljno podataka za generisanje grafikona.</div>
                )}
            </div>
        </div>
    );
};

export default PatientProgress;